"use client";
import { useState, useRef, useMemo, useEffect } from "react";
const ELEC_MIN = 5;
const ELEC_MAX = 20;

import Heading from "../Heading";

interface FuelCostCalculatorProps {
    isEV?: boolean;
    brandName: string;
    modelName: string;
    minMileage?: number;
    maxMileage?: number;
    minRange?: number;
    maxRange?: number;
    minBatteryCapacity?: number;
    maxBatteryCapacity?: number;
    defaultBatteryCapacity?: number;
    defaultMileageRange?: number;
}

function EditablePill({
    display,
    value,
    min,
    max,
    step = 1,
    suffix = "",
    onChange,
}: {
    display: string;
    value: number;
    min: number;
    max: number;
    step?: number;
    suffix?: string;
    onChange: (v: number) => void;
}) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState("");
    const ref = useRef<HTMLInputElement>(null);
    useEffect(() => {
        if (editing && ref.current) {
            ref.current.focus();
            ref.current.select();
        }
    }, [editing]);

    function open() {
        setDraft(String(value));
        setEditing(true);
    }

    function commit() {
        const p = parseFloat(draft);
        if (!isNaN(p)) onChange(p);
        setEditing(false);
    }

    if (editing) {
        return (
            <input
                ref={ref}
                type="number"
                inputMode="decimal"
                autoFocus
                value={draft}
                min={min}
                max={max}
                step={step}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => e.key === "Enter" && commit()}
                className="w-24 text-center text-[12px] font-semibold text-[#101828] bg-[#F0FBF8] border border-[#1A9E8F] rounded-full px-2.5 py-[3px] outline-none"
            />
        );
    }

    return (
        <span
            onClick={open}
            className="inline-flex items-center bg-[#F0FBF8] border border-[#A7DDCF] rounded-full px-2.5 py-[3px] text-[12px] text-[#008C8C] cursor-text hover:border-[#1A9E8F] transition-colors select-none whitespace-nowrap gap-1 font-bold"
        >
            {display}
            {suffix ? (
                <span className="text-[#717182] !font-normal">{suffix}</span>
            ) : null}
        </span>
    );
}

function SliderCard({
    label,
    value,
    min,
    max,
    step = 1,
    minLabel,
    maxLabel,
    errLabel,
    display,
    suffix = "",
    onChange,
}: {
    label: string;
    value: number;
    min: number;
    max: number;
    step?: number;
    minLabel: string;
    maxLabel: string;
    errLabel: string;
    display: string;
    suffix?: string;
    onChange: (v: number) => void;
}) {
    const [pillVal, setPillVal] = useState(value);
    const pct = ((value - min) / (max - min)) * 100;
    const atMax = !!pillVal && pillVal > max;
    const atMin = pillVal < min;

    return (
        <div className="">
            <div className="bg-white border border-[#E5E7EB] rounded-[8px] p-[12px] md:p-[16px]">
                {/* header */}
                <div className="flex items-center justify-between gap-[16px] mb-2.5">
                    <span className="text-[12px] text-[#6B7280] leading-tight font-medium">
                        {label}
                    </span>
                    <EditablePill
                        display={display}
                        value={value}
                        min={min}
                        max={max}
                        step={step}
                        suffix={suffix}
                        onChange={(v) => {
                            setPillVal(v)
                            onChange(v);
                        }}
                    />
                </div>

                {/* track */}
                <div className="relative h-[6px] rounded-full bg-[#E5E7EB] mb-2">
                    <div
                        className="absolute inset-y-0 left-0 rounded-full bg-[#1A9E8F] pointer-events-none"
                        style={{ width: `${pct}%` }}
                    />
                    <input
                        type="range"
                        min={min}
                        max={max}
                        step={step}
                        value={value}
                        onChange={(e) => {
                            const value = parseFloat(e.target.value);
                            setPillVal(value)
                            onChange(value);
                        }}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer m-0"
                    />
                    <div
                        className="absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-[#1A9E8F] border-[2.5px] border-[#1A9E8F] shadow pointer-events-none"
                        style={{ left: `calc(${pct}% - 8px)` }}
                    />
                </div>

                {/* min/max */}
                <div className="flex justify-between text-[11px] text-[#9CA3AF] font-medium">
                    <span>{minLabel}</span>
                    <span>{maxLabel}</span>
                </div>
            </div>
            {/* validation */}
            {atMax && (
                <p className="text-[11px] text-[#EF4444] mt-1.5">
                    Value cannot exceed{" "}
                    <span className="font-semibold">{errLabel}</span>
                </p>
            )}
            {atMin && (
                <p className="text-[11px] text-[#EF4444] mt-1.5">
                    Value cannot be less than{" "}
                    <span className="font-semibold">{minLabel}</span>
                </p>
            )}
        </div>
    );
}

function Stat({ val, label }: { val: number; label: string }) {
    return (
        <div className="flex-1 flex flex-col items-center justify-center py-[14px] px-2">
            <span className="text-[14px] md:text-[22px] font-bold text-[#1A9E8F] leading-tight">
                ₹{val.toLocaleString("en-IN")}
            </span>
            <span className="font-normal text-[12px] md:text-[14px] leading-[16px] tracking-[-0.25px] text-center capitalize text-[#6B7280] mt-0.5">
                {label}
            </span>
        </div>
    );
}

export default function FuelCostCalculator({
    isEV = false,
    brandName,
    modelName,
    minMileage = 0,
    maxMileage = 0,
    minRange = 0,
    maxRange = 0,
    minBatteryCapacity = 0,
    maxBatteryCapacity = 0,
    defaultBatteryCapacity = 0,
    defaultMileageRange = 0
}: FuelCostCalculatorProps) {
    const [km, setKm] = useState(50);
    const [fuel, setFuel] = useState(100);
    const [batteryCapacity, setBatteryCapacity] = useState(defaultBatteryCapacity);
    const [mi, setMi] = useState(defaultMileageRange); //mileage/range

    const DEFAULT_ELEC_PRICE = 8;
    const [elec, setElec] = useState<number>(DEFAULT_ELEC_PRICE); // selected value of electricity price per unit
    const [elecPillVal, setElecPillVal] = useState(DEFAULT_ELEC_PRICE); //refined value of electricity price per unit

    // set correct value 
    const clamp = (v: number, mn: number, mx: number, dec = 0) => {
        const c = Math.min(mx, Math.max(mn, v));
        return dec > 0 ? parseFloat(c.toFixed(dec)) : Math.ceil(c);
    };

    const { daily, monthly, yearly } = useMemo(() => {
        const rawDaily = mi > 0
            ? isEV
                ? (batteryCapacity / mi) * elec * km
                : (km / mi) * fuel
            : 0;

        // Truncate to one decimal point 
        const roundedDaily = Math.floor(rawDaily * 10) / 10;

        return {
            daily: Math.round(rawDaily),
            monthly: Math.round(roundedDaily * 30),
            yearly: Math.round(roundedDaily * 365),
        };
    }, [mi, isEV, batteryCapacity, elec, km, fuel]);

    const elecPct = ((elec - ELEC_MIN) / (ELEC_MAX - ELEC_MIN)) * 100;
    const elecAtMax = !!elecPillVal && elecPillVal > ELEC_MAX;
    const elecAtMin = elecPillVal < ELEC_MIN;

    return (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-5 md:p-6">
            {/* Header */}
            <div className="mb-3">
                <Heading tag="h2" className="font-bold text-[#101828]">
                    {`${brandName} ${modelName} Fuel Cost Calculator`}
                </Heading>
                <p className="font-normal text-[14px] leading-[20px] tracking-[0px] text-[#717182] mt-0.5">
                    Estimate your car&apos;s running cost
                </p>
            </div>

            {/* 3 sliders */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-[16px] mb-3">
                <SliderCard
                    label="Kms Driven per day"
                    value={km}
                    min={1}
                    max={1000}
                    display={`${km}`}
                    suffix=" km"
                    minLabel="1km"
                    maxLabel="1000km"
                    errLabel="1000km"
                    onChange={(v) => setKm(clamp(v, 1, 1000))}
                />

                {isEV && (
                    <SliderCard
                        label="Battery Capacity"
                        value={batteryCapacity}
                        min={minBatteryCapacity}
                        max={maxBatteryCapacity}
                        // step={0.01}
                        display={`${batteryCapacity} kWh`}
                        suffix=""
                        minLabel={`${minBatteryCapacity} kWh`}
                        maxLabel={`${maxBatteryCapacity} kWh`}
                        errLabel={`${maxBatteryCapacity} kWh`}
                        onChange={(v) =>
                            setBatteryCapacity(
                                clamp(
                                    v,
                                    minBatteryCapacity,
                                    maxBatteryCapacity
                                ),
                            )
                        }
                    />
                )}

                {!isEV && (
                    <SliderCard
                        label="Fuel Price"
                        value={fuel}
                        min={70}
                        max={130}
                        // step={0.01}
                        display={`₹ ${fuel}`}
                        suffix=""
                        minLabel="₹70/L"
                        maxLabel="₹130/L"
                        errLabel="₹130/L"
                        onChange={(v) => setFuel(clamp(v, 70, 130))}
                    />
                )}

                {isEV && (
                    <SliderCard
                        label="Range"
                        value={mi}
                        min={minRange}
                        max={maxRange}
                        display={`${mi}`}
                        suffix=" km"
                        minLabel={`${minRange} Km`}
                        maxLabel={`${maxRange} Km`}
                        errLabel={`${maxRange} km`}
                        onChange={(v) => setMi(clamp(v, minRange, maxRange))}
                    />
                )}

                {!isEV && (
                    <SliderCard
                        label="Mileage"
                        value={mi}
                        // step={0.01}
                        min={minMileage}
                        max={maxMileage}
                        display={`${mi}`}
                        suffix=" km/L"
                        minLabel={`${minMileage} Km/L`}
                        maxLabel={`${maxMileage} Km/L`}
                        errLabel={`${maxMileage} km/L`}
                        onChange={(v) => setMi(clamp(v, minMileage, maxMileage))}
                    />
                )}
            </div>

            {/* Electricity price */}
            {isEV && (
                <div className="mb-4">
                    <div className="bg-white border border-[#E5E7EB] rounded-[8px] md:rounded-[14px] p-[12px] md:p-[16px]">
                        <div className="flex items-center justify-between gap-2 mb-2.5">
                            <span className="text-[13px] font-semibold text-[#101828]">
                                Electricity Price per Unit
                            </span>
                            <EditablePill
                                display={`₹${elec.toFixed(2)}`}
                                value={elec}
                                min={ELEC_MIN}
                                max={ELEC_MAX}
                                // step={0.01}
                                suffix="/kWh"
                                onChange={(v) => {
                                    setElecPillVal(v);
                                    setElec(clamp(v, ELEC_MIN, ELEC_MAX));
                                }}
                            />
                        </div>

                        {/* track */}
                        <div className="relative h-[6px] rounded-full bg-[#E5E7EB] mb-2">
                            <div
                                className="absolute inset-y-0 left-0 rounded-full bg-[#1A9E8F] pointer-events-none"
                                style={{ width: `${elecPct}%` }}
                            />
                            <input
                                type="range"
                                min={ELEC_MIN}
                                max={ELEC_MAX}
                                // step={0.01}
                                value={elec}
                                onChange={(e) => {
                                    const value = parseFloat(e.target.value);
                                    setElecPillVal(value)
                                    setElec(
                                        clamp(
                                            value,
                                            ELEC_MIN,
                                            ELEC_MAX
                                        ),
                                    );
                                }}
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer m-0"
                            />
                            <div
                                className="absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-[#1A9E8F] border-[2.5px] border-[#1A9E8F] shadow pointer-events-none"
                                style={{ left: `calc(${elecPct}% - 8px)` }}
                            />
                        </div>

                        <div className="flex justify-between text-[11px] text-[#9CA3AF] font-medium">
                            <span>₹{ELEC_MIN}/kWh</span>
                            <span>₹{ELEC_MAX}/kWh</span>
                        </div>
                    </div>
                    {elecAtMax && (
                        <p className="text-[11px] text-[#EF4444] mt-1.5">
                            Value cannot exceed{" "}
                            <span className="font-semibold">
                                ₹{ELEC_MAX}/kWh
                            </span>
                        </p>
                    )}
                    {elecAtMin && (
                        <p className="text-[11px] text-[#EF4444] mt-1.5">
                            Value cannot be less than{" "}
                            <span className="font-semibold">
                                ₹{ELEC_MIN}/kWh
                            </span>
                        </p>
                    )}
                </div>
            )}

            {/* Result bar */}
            <div className="mb-3 block md:hidden text-center">
                <span className="text-[12px] font-bold text-[#000]">
                    Fuel Cost
                </span>
            </div>
            <div className="flex items-stretch border border-[#008C8B26] rounded-[10px] overflow-hidden mb-4 bg-[#008C8B0D]">
                <div className="px-5 py-4 min-w-[142px] hidden md:flex items-center justify-center">
                    <span className="text-[22px] font-bold text-[#101828]">
                        Fuel Cost
                    </span>
                </div>

                <div className="w-px self-stretch bg-gray-200 my-4 hidden md:block" />
                <Stat val={daily} label="Per Day" />
                <div className="w-px self-stretch bg-gray-200 my-4" />
                <Stat val={monthly} label="Per Month" />
                <div className="w-px self-stretch bg-gray-200 my-4" />
                <Stat val={yearly} label="Per Year" />
            </div>

            {/* Footer */}
            <p className="text-[9px] md:text-[11px] text-[#9CA3AF] leading-relaxed">
                {isEV
                    ? "*EV cost figures are indicative, basis Electric Vehicle's mileage and electricity cost"
                    : "*Costs may vary based on actual fuel prices and driving conditions.  Please verify specifications with the manufacturer for accuracy."}
            </p>
        </div>
    );
}
